import { Box, Header, Icon, Link, Popover, SpaceBetween, StatusIndicator, Table } from '@amzn/awsui-components-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  FlowBucketName,
  type AssetBucketDeals,
  type AssetFlowMetrics,
  type AssetFlowRow,
  type AssetOpportunity,
  type AssetVitalityBand,
  type KpiBucket,
} from '@amzn/ivthub-client';
import { ABSENT_MARK, formatArr, linearScale, maxValue } from './scale';
import { TREND_TRACK_PX, TrendBars } from './TrendBars';

/**
 * 我的资产清单 — one row per (asset, industry) from `GetAssetPipelineFlow`.
 *
 * ⭐ ROWS DO NOT SUM. Two assets sharing an `sfdc_tag` both claim the same opportunity, so
 * no column adds up to anything. There is deliberately NO totals row and NO column footer;
 * the page's total comes from the response's own `total`, which the caller owns.
 *
 * ⭐ The six buckets are not a partition either — `open` = momentum + base and
 * `notFollowedUp` is a subset of `open` — so one deal appears in several cells and in
 * several lines of the named-deals column. That is the contract, not duplication.
 */

/** The row is keyed on the PAIR: one asset under two industries' use cases yields two rows. */
const rowKey = (r: AssetFlowRow) => `${r.assetId ?? ''}\u0000${r.industry ?? ''}`;

/** GREEN / AMBER / RED, labelled as the industry table's legend labels them. */
const BAND: Record<AssetVitalityBand, { type: 'success' | 'warning' | 'error'; label: string }> = {
  GREEN: { type: 'success', label: '活跃' },
  AMBER: { type: 'warning', label: '低活' },
  RED: { type: 'error', label: '停摆' },
};

/**
 * Bucket order for the named-deals lines, matching the column order so the cell reads in the
 * same sequence as the row above it. This orders the LINES only — the deals inside a line keep
 * the server's order, which is the ARR-descending prefix `ListAssetOpportunities` pages.
 */
const BUCKET_ORDER: FlowBucketName[] = [
  FlowBucketName.MOMENTUM,
  FlowBucketName.BASE,
  FlowBucketName.OPEN,
  FlowBucketName.LAUNCHED,
  FlowBucketName.NOT_FOLLOWED_UP,
  FlowBucketName.LOST,
];

/** Short forms, as the design writes them in the cell — not the column headers' longer ones. */
const BUCKET_LABEL: Record<FlowBucketName, string> = {
  MOMENTUM: '后劲',
  BASE: '底盘',
  OPEN: '总在途',
  LAUNCHED: '上线',
  NOT_FOLLOWED_UP: '未跟进',
  LOST: '丢单',
};

/**
 * The bucket label's tone in a named-deals line — the SAME tones, and the same hexes, the KPI band
 * gives these buckets (`.is-positive` / `.is-caution` / `.is-negative` in DashboardPage.css), so a
 * colour means one thing on the page. The other three stay in the body colour; every label is bold.
 */
const BUCKET_TONE: Partial<Record<FlowBucketName, 'positive' | 'caution' | 'negative'>> = {
  MOMENTUM: 'positive',
  NOT_FOLLOWED_UP: 'caution',
  LOST: 'negative',
};

/** Unknown bucket values sort last rather than vanishing; `sort` is stable, so they keep order. */
const bucketRank = (b: FlowBucketName | undefined) => {
  const i = BUCKET_ORDER.indexOf(b as FlowBucketName);
  return i < 0 ? BUCKET_ORDER.length : i;
};

/**
 * `AstraZeneca $2.40M`.
 *
 * `accountName` may be absent — the tagged account can have no `account_cache` row, which the
 * contract says is a state to expose rather than an error — so it falls back to the opportunity's
 * own name before giving up, keeping the amount attached to something nameable.
 *
 * An absent `arr` prints a dash rather than `$0`: `opportunity_cache.arr` has no NOT NULL, so
 * absence means unrecorded, and `$0` would assert a figure nobody captured. (The bucket TOTALS
 * above are different — the server summed those, so their `$0` is an answer.)
 */
const dealText = (d: AssetOpportunity) =>
  `${d.accountName ?? d.oppName ?? ABSENT_MARK} ${d.arr == null ? ABSENT_MARK : formatArr(d.arr)}`;

/** Gap between the cell and its hover panel, and the panel's margin from the window edge. */
const DEALS_TIP_GAP_PX = 8;

interface DealsTipAnchor {
  /** Viewport coordinates of the cell: the panel hangs off its right edge. */
  right: number;
  top: number;
  bottom: number;
}

/** `18 opps` over `$10.25M`; the count is the drill-down handle when the caller wants one. */
function MetricCell({
  bucket,
  label,
  absentIsUnknown,
  tone,
  onClick,
}: {
  bucket: KpiBucket | undefined;
  label: string;
  /**
   * Set only on `lost`, the contract's one optional bucket, where absence means the deployed
   * service predates the field rather than "nothing was lost". The other five are `@required`,
   * so their 0 fallback is the right answer.
   */
  absentIsUnknown?: true;
  /**
   * Color class for a measured cell (DAT-9259 item 4): 'negative' paints 丢单 in #db0000, matching
   * the KPI band's 丢单 cell. is-absent REPLACES the tone — a dash on a character that carries no
   * value must not read as an alarm about nothing (same rule KpiBand keeps).
   */
  tone?: 'negative';
  onClick?: () => void;
}) {
  if (absentIsUnknown === true && bucket === undefined) {
    // No drill-down either: there is no list to open for a bucket this server does not compute.
    // The unit is dropped with the number — "— opps" reads as a count of dashes — so the label
    // has to reach assistive tech through aria-label, a bare dash announcing as nothing.
    return (
      <div className="ivt-dashboard__cell">
        <div className="ivt-dashboard__cell-count is-absent" aria-label={`${label}：服务端暂未提供该指标`}>
          {ABSENT_MARK}
        </div>
        <div className="ivt-dashboard__cell-arr is-absent">{ABSENT_MARK}</div>
      </div>
    );
  }
  const toneClass = tone ? ` is-${tone}` : '';
  const count = `${bucket?.oppCount ?? 0} opps`;
  return (
    <div className="ivt-dashboard__cell">
      {/* Plain text without a callback: an underline is the web's navigation affordance, and
          the drill-down is a later slice, so promising it before it exists would be a lie. */}
      <div className={`ivt-dashboard__cell-count${toneClass}`}>
        {onClick ? <Link onFollow={onClick}>{count}</Link> : count}
      </div>
      <div className={`ivt-dashboard__cell-arr${toneClass}`}>{formatArr(bucket?.arr)}</div>
    </div>
  );
}

/**
 * The 本期具名单子 cell: one capped, ellipsised line per bucket, and ONE hover panel for the whole
 * cell that shows the cell's lines again, uncut — whichever line the pointer is on. The panel adds
 * nothing the cell does not have; it only undoes the cap (user, 2026-09-30: "展示那个单元格被省略的
 * 全部内容即可").
 *
 * The panel follows TrendBars' mechanism and look (see its header for why): a portal into
 * document.body, `position: fixed` from the cell's own rect, because the Cloudscape cell's
 * `overflow: hidden` clips anything in flow; closed on scroll, since a fixed panel would drift
 * off its cell. Two differences, both forced by this being the table's LAST column and a list of
 * any length:
 *   - it hangs off the cell's RIGHT edge (pinned by `right`) rather than centring on it, so it
 *     cannot run off the right of the window;
 *   - it opens BELOW the cell and flips above only when the window has no room for it — decided
 *     from the panel's measured height (a layout effect, before paint), not a constant like
 *     TrendBars' TIP_HEIGHT_PX, because the height here depends on how many deals there are.
 */
function NamedDealsCell({ namedDeals }: { namedDeals: AssetBucketDeals[] | undefined }) {
  // An empty bucket produces no line — the contract omits it, and an empty one would read as
  // "nothing worth naming" rather than "nothing at all".
  const lines = (namedDeals ?? [])
    .filter((bd) => (bd.deals?.length ?? 0) > 0)
    .sort((a, b) => bucketRank(a.bucket) - bucketRank(b.bucket));

  const [anchor, setAnchor] = useState<DealsTipAnchor | null>(null);
  const [above, setAbove] = useState(false);
  const tipRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setAnchor(null), []);

  useEffect(() => {
    if (!anchor) return;
    window.addEventListener('scroll', close, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', close, { capture: true });
  }, [anchor, close]);

  // Before paint, so a flipped panel is never seen in its first position.
  useLayoutEffect(() => {
    if (!anchor || !tipRef.current) return;
    const h = tipRef.current.getBoundingClientRect().height;
    const roomBelow = window.innerHeight - anchor.bottom - DEALS_TIP_GAP_PX;
    const roomAbove = anchor.top - DEALS_TIP_GAP_PX;
    setAbove(h > roomBelow && roomAbove > roomBelow);
  }, [anchor]);

  if (lines.length === 0) return <Box color="text-status-inactive">{ABSENT_MARK}</Box>;

  // One rendering per line, shared by the cell and the panel so the two cannot say different things.
  const lineContent = (bd: AssetBucketDeals) => {
    const tone = BUCKET_TONE[bd.bucket as FlowBucketName];
    return (
      <>
        <span className={`ivt-dashboard__deal-bucket${tone ? ` is-${tone}` : ''}`}>
          {BUCKET_LABEL[bd.bucket as FlowBucketName] ?? bd.bucket}：
        </span>
        {bd.deals!.map(dealText).join('、')}
      </>
    );
  };

  return (
    <div
      className="ivt-dashboard__deals"
      onMouseEnter={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setAbove(false);
        setAnchor({ right: r.right, top: r.top, bottom: r.bottom });
      }}
      onMouseLeave={close}
    >
      <SpaceBetween size="xxs">
        {lines.map((bd) => (
          // Capped like the asset column, and for the same reason: one bucket with several long
          // account names widened the column for every row. The full text is in the DOM, so
          // assistive tech reads it whole; the panel below is for the eye only.
          <div key={bd.bucket ?? ''} className="ivt-dashboard__deal-line">
            <Box variant="span" fontSize="body-s">
              {lineContent(bd)}
            </Box>
          </div>
        ))}
      </SpaceBetween>

      {anchor &&
        createPortal(
          // `aria-hidden` for the reason TrendBars gives: the cell's own text already carries
          // every name and amount, so announcing the panel too would read them twice.
          <div
            ref={tipRef}
            className={`ivt-dashboard__deals-tip${above ? ' is-above' : ''}`}
            // ⭐ Pinned by `right`, not by `left` plus a -100% shift. A fixed box sized to its
            // content only gets the room between its `left` and the window's right edge, and this
            // cell's right edge IS near the window's — so a `left` anchor squeezed the panel to its
            // min-width and wrapped every line. `clientWidth`, not `innerWidth`: the fixed box's
            // containing block excludes the scrollbar, and the cell's rect is measured the same way.
            style={{
              right: document.documentElement.clientWidth - anchor.right,
              top: above ? anchor.top - DEALS_TIP_GAP_PX : anchor.bottom + DEALS_TIP_GAP_PX,
            }}
            aria-hidden="true"
          >
            {lines.map((bd) => (
              <div key={bd.bucket ?? ''} className="ivt-dashboard__deals-tip-line">
                {lineContent(bd)}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

export interface AssetTableProps {
  rows: AssetFlowRow[];
  /**
   * Drill-down to the bucket's full list (`ListAssetOpportunities`). Unwired here on purpose:
   * accepting it gives the page a seam, implementing it would make this component do two jobs.
   * `bucket` is the request-side enum, so the caller can pass it straight through.
   */
  onBucketClick?: (assetId: string, industry: string, bucket: FlowBucketName) => void;
}

export function AssetTable({ rows, onBucketClick }: AssetTableProps) {
  // ⭐ ONE scale for both trend columns and every row — the largest single month anywhere in the
  // table. Scaling per row or per column would make each row's busiest month full height and
  // erase the comparison between assets, which is what the column is for.
  const trendPx = linearScale(
    maxValue(
      rows.flatMap((r) =>
        (r.monthly ?? []).flatMap((m) => [m.momentum?.oppCount ?? 0, m.launched?.oppCount ?? 0]),
      ),
    ),
    TREND_TRACK_PX,
  );

  const metric = (
    id: string,
    header: ReactNode,
    label: string,
    pick: (m: AssetFlowMetrics | undefined) => KpiBucket | undefined,
    bucket: FlowBucketName,
    absentIsUnknown?: true,
    tone?: 'negative',
  ) => ({
    id,
    header,
    cell: (r: AssetFlowRow) => (
      <MetricCell
        bucket={pick(r.metrics)}
        label={label}
        absentIsUnknown={absentIsUnknown}
        tone={tone}
        onClick={
          onBucketClick && r.assetId && r.industry
            ? () => onBucketClick(r.assetId!, r.industry!, bucket)
            : undefined
        }
      />
    ),
  });

  return (
    <Table<AssetFlowRow>
      variant="container"
      contentDensity="comfortable"
      items={rows}
      trackBy={rowKey}
      header={
        <div className="ivt-dashboard__table-title">
          <Header variant="h2">我的资产清单</Header>
        </div>
      }
      columnDefinitions={[
        {
          id: 'asset',
          header: '资产',
          minWidth: 200,
          // The row's header cell, so assistive tech reads "<asset>, 后劲, 18 opps" rather than
          // a string of unattributed numbers. Cloudscape infers this from nothing.
          isRowHeader: true,
          cell: (r) => {
            // `name` is optional. The fallback is `assetId` — a stable kebab-case slug, so the
            // row stays identifiable — rather than a blank or a placeholder.
            const name = r.name ?? r.assetId ?? ABSENT_MARK;
            // The subtitle is the row's industry, which is what tells apart two rows for one asset
            // under two industries (names are unique across assets, so the name does the rest).
            // No assetId: the slug is an internal key, not something a reader looks an asset up
            // by. No use case either — `AssetFlowRow` carries none, and one row can stand for
            // several of the industry's use cases, so it would need a list, not a member.
            const industry = r.industry ?? ABSENT_MARK;
            // ⭐ Width-capped, both lines: one long asset name otherwise widened the column for
            // every row and pushed the metrics off to the right. What the cap cuts off is
            // ellipsised and read back through the native `title` tooltip on hover — the
            // package's existing pattern for a truncated name (see UseCasesPage's
            // `.ivt-ucdp__company-name`).
            return (
              <div>
                <div className="ivt-dashboard__cell-industry ivt-dashboard__asset-line" title={name}>
                  {/* To the asset's detail page, in a NEW TAB (user, 2026-09-30) so the dashboard
                      — its window, industry and paging — is still there to come back to. Cloudscape
                      adds `rel="noopener noreferrer"` for `_blank` itself. No `external` icon:
                      the page is inside the portal, and the icon on every row is noise.
                      No id, no link — there is nowhere to go. */}
                  {r.assetId ? (
                    <Link
                      variant="primary"
                      fontSize="inherit"
                      href={`/ivt/assets/${encodeURIComponent(r.assetId)}`}
                      target="_blank"
                    >
                      {name}
                    </Link>
                  ) : (
                    name
                  )}
                </div>
                <div className="ivt-dashboard__asset-line" title={industry}>
                  <Box variant="span" fontSize="body-s" color="text-body-secondary">
                    {industry}
                  </Box>
                </div>
              </div>
            );
          },
        },
        {
          id: 'band',
          header: '活跃度',
          cell: (r) => {
            const band = r.band ? BAND[r.band] : undefined;
            return band ? (
              <StatusIndicator type={band.type}>{band.label}</StatusIndicator>
            ) : (
              <Box color="text-status-inactive">{ABSENT_MARK}</Box>
            );
          },
        },
        metric('momentum', '后劲', '后劲', (m) => m?.momentum, FlowBucketName.MOMENTUM),
        metric('base', '底盘', '底盘', (m) => m?.base, FlowBucketName.BASE),
        metric('launched', '上线', '上线', (m) => m?.launched, FlowBucketName.LAUNCHED),
        metric(
          'notFollowedUp',
          // ⭐ THE ONE COLUMN NOT SCOPED TO THE REQUESTED WINDOW: the staleness clock is anchored
          // on TODAY, so asking about Q1 reports which of those opportunities is neglected now.
          // Nothing about the cell shows that, hence the note. 30 days is a server constant.
          <span>
            30 天未更新{' '}
            <Popover
              dismissButton={false}
              position="top"
              size="small"
              content={
                <span>
                  以今天为基准，不随所选窗口变化 —— 其余各列都跟随窗口。它也是总在途 Open 的子集，
                  所以不会超过 后劲 + 底盘。
                </span>
              }
            >
              <Icon name="status-info" variant="link" />
            </Popover>
          </span>,
          '30 天未更新',
          (m) => m?.notFollowedUp,
          FlowBucketName.NOT_FOLLOWED_UP,
        ),
        // ⭐ THE ONLY OPTIONAL BUCKET, so an older deployed service omits it and the cell prints a
        // dash rather than 0 — absence is "the server predates this field", not "nothing was lost".
        // Tone 'negative' paints a MEASURED cell in #db0000 (DAT-9259 item 4); an absent cell
        // stays grey (is-absent REPLACES the tone — see MetricCell).
        metric('lost', '丢单', '丢单', (m) => m?.lost, FlowBucketName.LOST, true, 'negative'),
        // ⚠️ These two are NOT scoped to the window either: `monthly` is anchored on endDate
        // alone, so for a longer window the three points sum to less than the row's metrics.
        {
          id: 'momentumTrend',
          header: '月度后劲',
          minWidth: 140,
          cell: (r) => <TrendBars monthly={r.monthly} bucket="momentum" px={trendPx} />,
        },
        {
          id: 'launchedTrend',
          header: '月度上线',
          minWidth: 140,
          cell: (r) => <TrendBars monthly={r.monthly} bucket="launched" px={trendPx} />,
        },
        {
          id: 'namedDeals',
          header: '本期具名单子',
          minWidth: 300,
          cell: (r) => <NamedDealsCell namedDeals={r.namedDeals} />,
        },
      ]}
      empty={
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'm' }}>
          没有可展示的资产
        </Box>
      }
    />
  );
}
